/** Fecha y hora en locale es-AR, zona fija America/Argentina/Buenos_Aires (producto AR). */

export const VUELTO_DISPLAY_TZ = 'America/Argentina/Buenos_Aires';

const LOCALE = 'es-AR';
const EMPTY_LABEL = '—';

const DATE_FORMATTER = new Intl.DateTimeFormat(LOCALE, {
  timeZone: VUELTO_DISPLAY_TZ,
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

const TIME_FORMATTER = new Intl.DateTimeFormat(LOCALE, {
  timeZone: VUELTO_DISPLAY_TZ,
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

function parseIso(iso: string | null | undefined): Date | null {
  if (iso == null || iso === '') {
    return null;
  }
  try {
    // PostgREST a veces manda "2026-08-29 21:23:57.421+00" sin T — normalizar.
    const withT =
      /^\d{4}-\d{2}-\d{2} /.test(iso) && !iso.includes('T') ? iso.replace(' ', 'T') : iso;
    // V8 exige offset con minutos: "+00" → "+00:00".
    const normalized = withT.replace(/([+-]\d{2})$/, '$1:00');
    const d = new Date(normalized);
    if (Number.isNaN(d.getTime())) {
      return null;
    }
    return d;
  } catch {
    return null;
  }
}

function invalidLabel(iso: string | null | undefined): string {
  if (iso == null || iso === '') {
    return EMPTY_LABEL;
  }
  return String(iso);
}

export function formatLocalDate(iso: string | null | undefined): string {
  const d = parseIso(iso);
  if (!d) {
    return invalidLabel(iso);
  }
  return DATE_FORMATTER.format(d);
}

export function formatLocalTime(iso: string | null | undefined): string {
  const d = parseIso(iso);
  if (!d) {
    return invalidLabel(iso);
  }
  return TIME_FORMATTER.format(d);
}

export function formatLocalDateTime(iso: string | null | undefined): string {
  const d = parseIso(iso);
  if (!d) {
    return invalidLabel(iso);
  }
  return `${DATE_FORMATTER.format(d)} ${TIME_FORMATTER.format(d)}`;
}

const SHORT_DAY_FORMATTER = new Intl.DateTimeFormat(LOCALE, {
  timeZone: VUELTO_DISPLAY_TZ,
  day: 'numeric',
  month: 'short',
});

/**
 * Timestamp compacto para listados densos (Ventas, Pedidos):
 * mismo día → `Hoy 08:20`; ayer → `Ayer 14:05`; mismo año → `24 sept 08:20`;
 * otro año → fecha completa `24/09/2025 08:20`.
 */
export function formatRelativeDayTime(iso: string | null | undefined): string {
  const d = parseIso(iso);
  if (!d) {
    return invalidLabel(iso);
  }
  const time = TIME_FORMATTER.format(d);
  const dayKey = DATE_FORMATTER.format(d);
  const now = new Date();
  const nowKey = DATE_FORMATTER.format(now);
  if (dayKey === nowKey) return `Hoy ${time}`;
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (dayKey === DATE_FORMATTER.format(yesterday)) return `Ayer ${time}`;
  if (dayKey.slice(-4) === nowKey.slice(-4)) {
    return `${SHORT_DAY_FORMATTER.format(d).replace(/\./g, '')} ${time}`;
  }
  return `${dayKey} ${time}`;
}

const DATE_LABEL_FORMATTER = new Intl.DateTimeFormat(LOCALE, {
  timeZone: VUELTO_DISPLAY_TZ,
  day: '2-digit',
  month: 'long',
  year: 'numeric',
});

export const createTodayDate = (): Date => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

/** Parsea YYYY-MM-DD a Date local; inválido o vacío -> hoy. */
export const parseIsoDate = (value: string): Date => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!match) return createTodayDate();
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const candidate = new Date(year, month - 1, day);
  if (candidate.getFullYear() !== year || candidate.getMonth() + 1 !== month || candidate.getDate() !== day) {
    return createTodayDate();
  }
  return candidate;
};

export const formatIsoDate = (date: Date): string =>
  `${String(date.getFullYear()).padStart(4, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate()
  ).padStart(2, '0')}`;

/**
 * Convierte una fecha YYYY-MM-DD (local) a ISO start-of-day con offset local.
 * Evita el bug de midnight UTC que desplaza la fecha 3 horas en zonas UTC-3.
 */
export const toLocalStartOfDayIso = (dateStr: string): string => {
  if (!dateStr.trim()) return '';
  const offset = -new Date().getTimezoneOffset();
  const sign = offset >= 0 ? '+' : '-';
  const hours = String(Math.floor(Math.abs(offset) / 60)).padStart(2, '0');
  const minutes = String(Math.abs(offset) % 60).padStart(2, '0');
  return `${dateStr.trim()}T00:00:00${sign}${hours}:${minutes}`;
};

/**
 * Convierte una fecha YYYY-MM-DD (local) a ISO end-of-day con offset local.
 */
export const toLocalEndOfDayIso = (dateStr: string): string => {
  if (!dateStr.trim()) return '';
  const offset = -new Date().getTimezoneOffset();
  const sign = offset >= 0 ? '+' : '-';
  const hours = String(Math.floor(Math.abs(offset) / 60)).padStart(2, '0');
  const minutes = String(Math.abs(offset) % 60).padStart(2, '0');
  return `${dateStr.trim()}T23:59:59${sign}${hours}:${minutes}`;
};

export const formatDateLabelEsAr = (value: string): string => DATE_LABEL_FORMATTER.format(parseIsoDate(value));
